<template>
  <v-dialog
    v-model="visible"
    class="project-modal"
    scrollable
    max-width="750"
  >
    <v-card
      v-if="action === 'delete'"
      class="pa-6"
    >
      <v-card-title primary-title>
        <div class="headline darkestblue--text">
          Are you sure?
        </div>
        <v-card-actions>
          <v-btn
            :ripple="false"
            icon
            @click.stop="closeForm()"
          >
            <v-icon color="brightblue">
              close
            </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card-title>

      <v-card-text>
        <div class="coolgray--text">
          <v-row>
            <v-col class="subtitle-2">
              ID
            </v-col>
            <v-col class="body-1">
              {{ entry.project_id }}
            </v-col>
          </v-row>

          <v-row>
            <v-col class="subtitle-2">
              Project
            </v-col>
            <v-col class="body-1">
              {{ entry.project_name }}
            </v-col>
          </v-row>

          <v-row>
            <v-col class="subtitle-2">
              Code
            </v-col>
            <v-col class="body-1">
              {{ entry.project_code }}
            </v-col>
          </v-row>
        </div>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn
          text
          color="coolgray"
          @click.stop="cancelAction"
        >
          Cancel
        </v-btn>

        <v-btn
          color="primary"
          @click.stop="submitProjectAction"
        >
          Submit
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card
      v-if="['create', 'update'].includes(action)"
      class="pa-4"
    >
      <v-card-title>
        <span>
          {{ action === 'create' ? 'Add' : 'Edit' }} Project
        </span>

        <v-card-actions>
          <v-btn
            :ripple="false"
            icon
            @click.stop="closeForm()"
          >
            <v-icon color="brightblue">
              close
            </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card-title>

      <v-card-text>
        <v-form
          ref="projectForm"
          v-model="validForm"
        >
          <v-container grid-list-xs>
            <v-row>
              <v-col>
                <v-text-field
                  v-model="entry.project_name"
                  label="Project Name"
                  name="project_name"
                  type="textual"
                />

                <v-text-field
                  v-model="entry.project_code"
                  label="Project Code"
                  name="project_code"
                  type="textual"
                  width="150px"
                />

                <v-text-field
                  v-model="entry.billingCode"
                  label="Billing Code"
                  name="billingCode"
                  type="textual"
                  width="150px"
                />

                <!-- Select Project Type -->
                <v-select
                  v-model="entry.projectType"
                  :items="projectTypes"
                  item-text="text"
                  item-value="value"
                  label="Project Type"
                />

                <v-textarea
                  v-model="entry.projectDescription"
                  auto-grow
                  class="border-light py-3"
                  counter="250"
                  item-text="text"
                  item-value="value"
                  label="Brief Description"
                  name="description"
                  type="textual"
                  :rules="descriptionRules"
                  row-height="15"
                  rows="1"
                  @input="validateForm"
                />

                <!-- Select Project Status -->
                <v-row>
                  <v-col>
                    <v-select
                      v-model="entry.projectStatus"
                      :items="projectStatuses"
                      label="Status"
                    />
                  </v-col>
                </v-row>

                <v-row v-if="showThresholdFeature">
                  <v-col>
                    <v-btn
                      text
                      @click="showThresholdForm('create')"
                    >
                      <span>Add Threshold</span>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
        </v-form>

        <v-row>
          <v-col>
            <v-card-actions class="justify-end">
              <v-btn
                text
                color="coolgray"
                @click.stop="cancelAction"
              >
                Cancel
              </v-btn>

              <v-btn
                color="primary"
                @click.stop="submitProjectAction"
              >
                Submit
              </v-btn>
            </v-card-actions>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

const { mapGetters, mapState } = createNamespacedHelpers('internal')

export default {
  name: 'ProjectModal',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    loading: false,
    validForm: true,
    showThresholdFeature: false,
    thresholdForm: null,

    projectTypes: [
      {
        title: 'Programmatic',
        value: 'programmatic',
      },
      {
        title: 'Tagging',
        value: 'tagging',
      },
    ],

    projectStatuses: [
      {
        title: 'Scoping',
        value: 'scoping',
      },
      {
        title: 'Pending Sig',
        value: 'pending-signature',
      },
      {
        title: 'In Negotiation',
        value: 'in-negotiation',
      },
      {
        title: 'Launching',
        value: 'launching',
      },
      {
        title: 'In Progress',
        value: 'in-progress',
      },
      {
        title: 'Complete',
        value: 'complete',
      },
    ],
  }),
  computed: {
    visible: {
      get() {
        return this.isProjectModalVisible
      },
      set(value) {
        this.$store.commit('internal/isProjectModalVisible', value)
      },
    },
    ...mapGetters(['errorMessage', 'successMessage']),
    ...mapState({
      action: (state) => state.action,
      didLoadProjectFail: (state) => state.didLoadProjectFail,
      entry: (state) => state.entry,
      isLoadingProject: (state) => state.isLoadingProject,
      isProjectModalVisible: (state) => state.isProjectModalVisible,
      projects: (state) => state.projects,
      selected: (state) => state.selected,
    }),
    viewProject() {
      const {
        projects,
        didLoadProjectFail,
        isLoadingProject,
      } = this

      return isLoadingProject === false
        && didLoadProjectFail === false
        && projects.length > 0
    },
    noProjectData() {
      const {
        projects,
        isLoadingProject,
        didLoadProjectFail,
      } = this
      const requestComplete = isLoadingProject === false
      const requestSucceeded = didLoadProjectFail === false
      const noResults = projects.length === 0

      return requestComplete && requestSucceeded && noResults
    },
    descriptionRules() {
      return [
        (value) => {
          const valid = !value || value.length <= 250
          return valid || 'Description cannot exceed 250 characters.'
        },
      ]
    },
  },
  methods: {
    validateForm() {
      return this.$refs.projectForm.validate()
    },

    reset() {
      return this.$refs.projectForm.reset()
    },

    resetValidation() {
      return this.$refs.projectForm.resetValidation()
    },

    closeForm() {
      this.resetValidation()
      this.visible = false
    },

    hideThresholdForm() {
      this.thresholdForm = null
    },
    showThresholdForm(action, entryThreshold = {}) {
      const testing = true

      if (testing) {
        return testing
      }

      if (action === 'update') {
        this.thresholdForm = entryThreshold
      } else {
        this.thresholdForm = {
          // Identifiers
          name: String,
          code: String, // User input of an alphanumeric value matching the TEST1234 pattern. Unique.

          // Change Logs
          updated: [{ date: Date, by: String }], // by === currentUser.email
          created: [{ date: Date, by: String }], // by === personUser.email

          // Dates
          startDate: Date,
          endDate: Date,

          watch: {
            collectionName: 'project', // Collection Name: String: [organization, person, project, service, team, time]
            property: 'projectCode', // Collection Code: String: [billingCode, personId, projectCode, serviceCode, teamCode]
            value: 'TEST1234', // A string to be used as a regex test on the given property, e.g. projectCode.
            operator: 'eq',
          },
          cause: [
            {
              dateRange: { startDate: Date, endDate: Date },
              // Update form input types and options based on context like page and user selections in the form.
              // Option Groups: [Boolean, Numerical, Textual, Strict (N/EQ), Custom]
              if: 'add', // Event: ['add', 'inactive', 'remove']
              is: true, // Value: Any: [user input, presets based on operator]
              for: 'yield partner', // ['demand source', 'network', 'yield partner']
              operator: 'and', // ['and', 'or']
            },
            {
              // @TODO Confirm operator at dateRange during user sessions.
              dateRange: { startDate: Date, endDate: Date }, // >= 14 days
              if: 'total',
              is: 0,
              for: 'delivery',
              operator: 'and', // ['and', 'or']
            },
          ],
          effect: [
            {
              then: 'email', // ['alert in-app', 'email', 'slack']
              endpoints: ['accounts@adops.com', 'bts@adops.com'], // [personUser.email]
              frequency: 'daily', // Frequency Cap: [once, daily, weekly, monthly]
              schedule: 'immediately', // [immediately, daily, weekly, monthly]
            },
            {
              then: 'email', // ['alert in-app', 'email', 'slack']
              endpoints: ['example.internal@adops.com'], // [personUser.email]
              frequency: 'once', // Frequency Cap: [daily, once]
              schedule: 'weekly', // [immediately, daily, weekly, monthly]
            },
          ],

          filter: [
            { operator: 'neq', property: 'name', value: 'Yield Partner A' },
            { operator: 'neq', property: 'type', value: 'Sponsorship' },
          ],
        }
      }

      return this.thresholdForm
    },
    hideProjectForm() {
      this.projectForm = null
    },
    showProjectForm(action, entryProject = {}) {
      if (action === 'update') {
        this.projectForm = entryProject
      } else {
        this.projectForm = {
          // Identifiers
          name: String,
          code: String,

          // Change Logs
          // Update change log separately to avoid performance issues.
          updated: [{
            // endpointName: 'project',
            // endpointCode: 'TEST2021',
            by: String, // by === currentUser.email
            date: Date,
            snapshot: String, // JSON.stringify(entry)
          }],

          // Dates
          startDate: Date,
          endDate: Date,

          contract: [
            {
              type: String, // []
              hours: Number,
            },
          ],
        }
      }
    },

    async submitProjectAction() {
      const { action, entry } = this
      const { commit, dispatch } = this.$store
      // @TODO: Actually map the action_type to what it's meant to be.
      const map = {
        create: 'ADD',
        update: 'UPDATE',
        delete: 'DELETE',
      }

      entry.action_type = map[action]

      /**
       * Send to Firebase.
       */

      const excludedProperties = ['project_code', 'project_id', 'project_name']
      const firebaseDoc = Object.keys(entry).reduce((obj, key) => {
        if (!excludedProperties.includes(key)) {
          obj[key] = entry[key]
        }

        return obj
      })

      // Upsert the document to Firestore.
      const fireDoc = await this.$store.dispatch('internal/upsertProject', firebaseDoc)

      /**
       * Send to API.
       */

      const apiResponse = await dispatch('internal/submitProjectAction', entry)
      const { content: apiContent = [], error: apiError } = apiResponse || {}

      if (apiError || apiContent?.code || !apiResponse) {
        const message = apiError?.message || apiContent?.message || apiContent?.code || 'Unknown API Error'

        commit('tools/snackbar', true)
        commit('tools/snack', { message, status: 'error' })

        return apiError || apiContent
      }

      const [entryId] = apiContent
      const apiStatus = entryId ? 'success' : 'error'
      const message = entryId ? this.successMessage : this.errorMessage

      if (apiStatus === 'success') {
        this.closeForm()
      }

      commit('tools/snackbar', true)
      commit('tools/snack', { message, status: apiStatus })

      return apiResponse
    },
    cancelAction() {
      const { selected } = this

      this.$store.commit('internal/action', 'read')
      this.$store.commit('internal/entry', Object.create(selected))

      this.closeForm()
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

::v-deep.v-card {
  border-radius: 6px;
  border-width: 0;
}

::v-deep.v-card__title {
  color: $color-monochrome-darkestblue;
  font-size: 18px;
  font-weight: bold;
  letter-spacing: 0.51px;
  line-height: 22px;
  justify-content: space-between;
}

::v-deep.v-card__text {
  padding: 0;
}

.v-card + .v-card {
  margin-top: 1ch;
}
</style>
